import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  alpha,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  Popover,
} from '@mui/material';
import {
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Receipt as ReceiptIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

function ProductGrid({ 
  products, 
  loading, 
  onSelectProduct, 
  showCustomProduct = true,
  isFullscreen = false,
  fullscreenContainer = null,
  onCustomProductClick,
}) {
  const { t } = useTranslation();
  const [quantities, setQuantities] = useState({});
  const [editedPrices, setEditedPrices] = useState({});
  const [priceEditMode, setPriceEditMode] = useState({});

  // ✅ NEW: State for image magnifier
  const [imagePopover, setImagePopover] = useState({
    open: false,
    anchorEl: null,
    imageUrl: null,
    productName: null,
  });

  const getQuantity = (productId) => {
    return quantities[productId] || 0;
  };

  const setQuantity = (productId, value) => {
    const newValue = Math.max(0, parseInt(value) || 0);
    setQuantities(prev => ({ ...prev, [productId]: newValue }));
  };

  const incrementQuantity = (productId) => {
    const currentQty = quantities[productId] || 0;
    setQuantities(prev => ({ ...prev, [productId]: currentQty + 1 }));
  };

  const decrementQuantity = (productId) => {
    const currentQty = quantities[productId] || 0;
    
    if (currentQty > 0) {
      setQuantities(prev => ({ 
        ...prev, 
        [productId]: currentQty - 1
      }));
    }
  };

  const getCurrentPrice = (product) => {
    if (editedPrices[product.Id] !== undefined) {
      return editedPrices[product.Id];
    }
    return product.SalePrice || product.Price || 0;
  };

  const handlePriceEdit = (productId, value) => {
    const price = parseFloat(value) || 0;
    setEditedPrices(prev => ({ ...prev, [productId]: price }));
  };

  const togglePriceEditMode = (productId) => {
    setPriceEditMode(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  const handleAddToCart = (product) => {
    const quantity = getQuantity(product.Id);
    if (quantity > 0) {
      const currentPrice = getCurrentPrice(product);
      const originalPrice = product.SalePrice || product.Price || 0;
      const isPriceEdited = editedPrices[product.Id] !== undefined;
      
      console.log('📦 ProductGrid - Adding to cart:', {
        productName: product.Name,
        originalPrice: originalPrice,
        currentPrice: currentPrice,
        isPriceEdited: isPriceEdited,
        quantity: quantity,
      });
      
      const productWithPrice = {
        ...product,
        Price: currentPrice,
        SalePrice: currentPrice,
        OriginalPrice: originalPrice,
        IsPriceEdited: isPriceEdited,
      };
      
      onSelectProduct(productWithPrice, quantity);
      
      setQuantities(prev => ({ ...prev, [product.Id]: 0 }));
      setPriceEditMode(prev => ({ ...prev, [product.Id]: false }));
      
      if (isPriceEdited) {
        toast.success(`${product.Name} added with custom price €${currentPrice.toFixed(2)}`);
      }
    }
  };

  const getProductImage = (product) => {
    if (product.ImageUrl) return product.ImageUrl;
    if (product.Images && product.Images.length > 0) return product.Images[0];
    return null;
  };

  const getStockColor = (stock) => {
    if (stock <= 0) return 'error';
    if (stock < 10) return 'warning';
    return 'success';
  };

  // ✅ NEW: Handle image hover for magnifier
  const handleImageMouseEnter = (event, imageUrl, productName) => {
    if (imageUrl) {
      setImagePopover({
        open: true,
        anchorEl: event.currentTarget,
        imageUrl: imageUrl,
        productName: productName,
      });
    }
  };

  const handleImageMouseLeave = () => {
    setImagePopover({
      open: false,
      anchorEl: null,
      imageUrl: null,
      productName: null,
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const hasNoProducts = !products || products.length === 0;

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Product Grid */}
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: { xs: 0.5, sm: 1 },
          pb: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: alpha('#ccc', 0.1),
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#3498db', 0.3),
            borderRadius: '10px',
            '&:hover': {
              background: alpha('#3498db', 0.5),
            },
          },
        }}
      >
        {hasNoProducts ? (
          <Box>
            {/* Custom Product Card */}
            {showCustomProduct && (
              <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
                <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                  <Card
                    onClick={() => onCustomProductClick?.()}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      borderRadius: 2,
                      overflow: 'visible',
                      boxShadow: 2,
                      cursor: 'pointer',
                      border: '2px dashed',
                      borderColor: '#667eea',
                      bgcolor: alpha('#667eea', 0.02),
                      minWidth: 0,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                        bgcolor: alpha('#667eea', 0.05),
                        borderColor: '#5568d3',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: { xs: 60, sm: 80 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha('#667eea', 0.08),
                      }}
                    >
                      <AddCircleOutlineIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: '#667eea' }} />
                    </Box>

                    <CardContent sx={{ p: { xs: 1, sm: 1.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          color: '#667eea',
                          textAlign: 'center',
                        }}
                      >
                        {t('add_new_product') || 'Add New Product'}
                      </Typography>
                      
                    
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* No Products Message */}
            <Box
              sx={{
                textAlign: 'center',
                py: { xs: 4, sm: 8 },
                color: 'text.secondary',
              }}
            >
              <InventoryIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                {t('no_products_found') || 'No products found'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {t('add_custom_product_hint') || 'You can still add custom products'}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
            {/* Custom Product Card */}
            {showCustomProduct && (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
                <Card
                  onClick={() => onCustomProductClick?.()}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s',
                    borderRadius: 2,
                    overflow: 'visible',
                    boxShadow: 2,
                    cursor: 'pointer',
                    border: '2px dashed',
                    borderColor: '#667eea',
                    bgcolor: alpha('#667eea', 0.02),
                    minWidth: 0,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                      bgcolor: alpha('#667eea', 0.05),
                      borderColor: '#5568d3',
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: { xs: 60, sm: 80 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha('#667eea', 0.08),
                    }}
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: '#667eea' }} />
                  </Box>

                  <CardContent sx={{ p: { xs: 1, sm: 1.5 }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.75rem', sm: '0.85rem' },
                        color: '#667eea',
                        textAlign: 'center',
                      }}
                    >
                      {t('add_new_product') || 'Add New Product'}
                    </Typography>
                    
                   
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Regular Products */}
            {products.map((product) => {
              const productImage = getProductImage(product);
              const currentQuantity = getQuantity(product.Id);
              const currentPrice = getCurrentPrice(product);
              const isPriceEditing = priceEditMode[product.Id];
              const isPriceEdited = editedPrices[product.Id] !== undefined;
              const maxStock = product.Stock || product.StockQuantity || 0;
              const canDecrement = currentQuantity > 0;
              const canAddToCart = currentQuantity > 0;
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={product.Id}>
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      borderRadius: 2,
                      overflow: 'hidden', // ✅ Changed from 'visible' to 'hidden' to prevent image overflow
                      boxShadow: 2,
                      border: isPriceEdited ? '2px solid #ff9800' : 'none',
                      minWidth: 0,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    {/* ✅ FIXED: Product Image with proper object-fit and hover magnifier */}
                    {productImage ? (
                      <Box
                        sx={{
                          height: { xs: 80, sm: 100 }, // ✅ Increased height for better visibility
                          width: '100%',
                          position: 'relative',
                          overflow: 'hidden',
                          bgcolor: '#f5f5f5',
                          cursor: 'zoom-in',
                        }}
                        onMouseEnter={(e) => handleImageMouseEnter(e, productImage, product.Name)}
                        onMouseLeave={handleImageMouseLeave}
                      >
                        <CardMedia
                          component="img"
                          image={productImage}
                          alt={product.Name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain', // ✅ Changed from 'cover' to 'contain' to show full image
                            objectPosition: 'center',
                            transition: 'transform 0.2s',
                            '&:hover': {
                              transform: 'scale(1.05)',
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: { xs: 80, sm: 100 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha('#667eea', 0.08),
                        }}
                      >
                        <ShoppingCartIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: alpha('#667eea', 0.4) }} />
                      </Box>
                    )}

                    <CardContent sx={{ p: { xs: 1, sm: 1.5 }, display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 0.75 } }}>
                      {/* Product Name */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.75rem', sm: '0.8rem' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.4,
                          minHeight: { xs: 32, sm: 36 },
                        }}
                        title={product.Name}
                      >
                        {product.Name}
                      </Typography>

                      {/* Price and Stock Section */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 0.75 } }}>
                        {/* Stock Chip */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Chip
                            label={`Stock: ${maxStock}`}
                            size="small"
                            color={getStockColor(maxStock)}
                            sx={{ 
                              fontWeight: 600, 
                              fontSize: { xs: '0.6rem', sm: '0.65rem' },
                              height: { xs: 18, sm: 20 },
                              minWidth: { xs: 50, sm: 60 },
                              '& .MuiChip-label': {
                                px: { xs: 0.5, sm: 1 },
                              }
                            }}
                          />
                        </Box>

                        {/* Price Section */}
                        {isPriceEditing ? (
                          <TextField
                            type="number"
                            value={currentPrice}
                            onChange={(e) => handlePriceEdit(product.Id, e.target.value)}
                            onBlur={() => togglePriceEditMode(product.Id)}
                            autoFocus
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">€</InputAdornment>,
                              style: { 
                                fontSize: { xs: '0.85rem', sm: '1rem' },
                                fontWeight: 700,
                                color: '#ff9800',
                              }
                            }}
                            inputProps={{ 
                              min: 0, 
                              step: 0.01,
                              style: { padding: '4px 6px' }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor: '#ff9800',
                                  borderWidth: 2,
                                },
                              },
                            }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: isPriceEdited ? '#ff9800' : 'primary.main',
                                fontWeight: 700,
                                fontSize: { xs: '0.85rem', sm: '1rem' },
                              }}
                            >
                              €{currentPrice.toFixed(2)}
                            </Typography>
                            <Tooltip title={t('edit_price') || 'Edit Price'}>
                              <IconButton
                                size="small"
                                onClick={() => togglePriceEditMode(product.Id)}
                                sx={{
                                  width: { xs: 20, sm: 24 },
                                  height: { xs: 20, sm: 24 },
                                  color: isPriceEdited ? '#ff9800' : '#999',
                                  '&:hover': {
                                    color: '#ff9800',
                                    bgcolor: alpha('#ff9800', 0.1),
                                  },
                                }}
                              >
                                <EditIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}

                        {/* Original price if edited */}
                        {isPriceEdited && !isPriceEditing && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              textDecoration: 'line-through',
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                            }}
                          >
                            Original: €{(product.SalePrice || product.Price || 0).toFixed(2)}
                          </Typography>
                        )}
                      </Box>

                      {/* Quantity Controls */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: { xs: 0.5, sm: 0.75 },
                          bgcolor: alpha('#667eea', 0.05),
                          borderRadius: 1.5,
                          p: { xs: 0.4, sm: 0.5 },
                          mt: { xs: 0.25, sm: 0.5 },
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => decrementQuantity(product.Id)}
                          disabled={!canDecrement}
                          sx={{
                            bgcolor: canDecrement ? '#ff5252' : '#ffcdd2',
                            color: 'white',
                            width: { xs: 24, sm: 28 },
                            height: { xs: 24, sm: 28 },
                            minWidth: { xs: 24, sm: 28 },
                            '&:hover': { 
                              bgcolor: canDecrement ? '#ff1744' : '#ffcdd2',
                            },
                            '&.Mui-disabled': { 
                              bgcolor: '#ffcdd2',
                              color: '#fff',
                              opacity: 0.6,
                            },
                            p: 0,
                          }}
                        >
                          <RemoveIcon sx={{ fontSize: { xs: 12, sm: 16 } }} />
                        </IconButton>

                        <TextField
                          type="number"
                          value={currentQuantity}
                          onChange={(e) => setQuantity(product.Id, e.target.value)}
                          inputProps={{ 
                            min: 0,
                            style: { 
                              textAlign: 'center',
                              padding: '3px 2px',
                              fontWeight: 700,
                              fontSize: window.innerWidth < 600 ? '0.75rem' : '0.85rem',
                            } 
                          }}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            '& .MuiOutlinedInput-root': {
                              height: { xs: 24, sm: 28 },
                              '& fieldset': { border: 'none' },
                            },
                            '& input': {
                              color: currentQuantity > 0 ? '#667eea' : '#999',
                            }
                          }}
                        />

                        <IconButton
                          size="small"
                          onClick={() => incrementQuantity(product.Id)}
                          sx={{
                            bgcolor: '#4caf50',
                            color: 'white',
                            width: { xs: 24, sm: 28 },
                            height: { xs: 24, sm: 28 },
                            minWidth: { xs: 24, sm: 28 },
                            '&:hover': { 
                              bgcolor: '#43a047',
                            },
                            p: 0,
                          }}
                        >
                          <AddIcon sx={{ fontSize: { xs: 12, sm: 16 } }} />
                        </IconButton>
                      </Box>

                      {/* Add to Cart Button */}
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        startIcon={<ShoppingCartIcon sx={{ fontSize: { xs: 12, sm: 16 } }} />}
                        onClick={() => handleAddToCart(product)}
                        disabled={!canAddToCart}
                        sx={{
                          py: { xs: 0.5, sm: 0.75 },
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          fontWeight: 600,
                          borderRadius: 1.5,
                          textTransform: 'none',
                          minHeight: { xs: 28, sm: 32 },
                          background: canAddToCart
                            ? isPriceEdited 
                              ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
                              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                            : '#e0e0e0',
                          color: canAddToCart ? 'white' : '#9e9e9e',
                          boxShadow: canAddToCart ? '0 2px 6px rgba(102, 126, 234, 0.3)' : 'none',
                          '&:hover': {
                            background: canAddToCart
                              ? isPriceEdited
                                ? 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)'
                                : 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' 
                              : '#e0e0e0',
                            boxShadow: canAddToCart ? '0 3px 10px rgba(102, 126, 234, 0.4)' : 'none',
                            transform: canAddToCart ? 'translateY(-1px)' : 'none',
                          },
                          '&.Mui-disabled': {
                            background: '#e0e0e0',
                            color: '#9e9e9e',
                          },
                        }}
                      >
                        {t('add_to_cart') || 'Add'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* ✅ NEW: Image Magnifier Popover */}
      <Popover
        open={imagePopover.open}
        anchorEl={imagePopover.anchorEl}
        onClose={handleImageMouseLeave}
        disableRestoreFocus
        disableScrollLock
        disableEnforceFocus
        disableAutoFocus
        sx={{
          pointerEvents: 'none', // ✅ Allows mouse to pass through
        }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            pointerEvents: 'none',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            borderRadius: 2,
            overflow: 'hidden',
            border: '2px solid #667eea',
            bgcolor: 'white',
          },
        }}
      >
        <Box
          sx={{
            width: { xs: 250, sm: 300, md: 350 },
            height: { xs: 250, sm: 300, md: 350 },
            position: 'relative',
            bgcolor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Product Name Header */}
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '0.85rem',
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {imagePopover.productName}
            </Typography>
          </Box>

          {/* Magnified Image */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <img
              src={imagePopover.imageUrl}
              alt={imagePopover.productName}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

export default ProductGrid;